import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { websocketService } from '../services';
import './GiftOverlay.css';

// API 基础 URL
const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5000/api' : '/api');

// 舰长图标映射
const guardIconMap = {
  '舰长': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/captain-Bjw5Byb5.png',
  '提督': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/supervisor-u43ElIjU.png',
  '总督': 'https://s1.hdslb.com/bfs/static/blive/live-pay-mono/relation/relation/assets/governor-DpDXKEdA.png'
};

// 大航海头像框映射 (guard_level: 1=总督, 2=提督, 3=舰长)
const guardFrameMap = {
  1: 'https://i0.hdslb.com/bfs/live/a454275dea465ac15a03f121f0d7edaf96e30bcf.png', // 总督
  2: 'https://i0.hdslb.com/bfs/live/09937c3beb0608e267a50ac3c7125c3f2d709098.png', // 提督
  3: 'https://i0.hdslb.com/bfs/live/80f732943cc3367029df65e267960d56736a82ee.png'  // 舰长
};

/**
 * 根据礼物价值获取等级 class
 */
const getGiftLevelClass = (totalCoin) => {
  const value = totalCoin / 1000; // 转换为元
  if (value >= 100) return 'level-gold';   // 金色 100元+
  if (value >= 15) return 'level-red';     // 红色 15-100元
  if (value >= 5) return 'level-purple';   // 紫色 5-15元
  return '';                                // 蓝色（默认）<5元
};

/**
 * OBS 礼物展示页面
 * 路由: /live/gifts/:accessToken
 * 
 * 通过 WebSocket 实时接收礼物更新
 * 透明背景，适合叠加在 OBS 上
 * 支持自动滚动（循环/往复模式）
 */
function GiftOverlay() {
  const { accessToken } = useParams();
  const [searchParams] = useSearchParams();
  
  // 预览模式
  const isPreview = searchParams.get('preview') === '1';
  
  // 状态
  const [roomId, setRoomId] = useState(null);
  const [config, setConfig] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [userFaces, setUserFaces] = useState({});
  const [userGuardLevels, setUserGuardLevels] = useState({}); // 用户大航海等级
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 滚动相关
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [shouldScroll, setShouldScroll] = useState(false);
  
  // 礼物更新队列（用于批量处理快速连续的礼物事件）
  const giftQueueRef = useRef([]);
  const processTimerRef = useRef(null);
  
  // 设置透明背景（兼容不支持 :has() 选择器的 OBS 浏览器）
  useEffect(() => {
    if (!isPreview) {
      // 非预览模式：设置透明背景
      document.documentElement.style.background = 'transparent';
      document.body.style.background = 'transparent';
      document.body.style.minHeight = 'auto';
    }
    
    return () => {
      // 组件卸载时恢复默认样式
      document.documentElement.style.background = '';
      document.body.style.background = '';
      document.body.style.minHeight = '';
    };
  }, [isPreview]);
  
  // 加载配置
  const fetchConfig = useCallback(async () => {
    const response = await fetch(`${API_BASE}/gift-overlay/obs/${accessToken}/config`);
    if (!response.ok) {
      throw new Error('获取配置失败');
    }
    return response.json();
  }, [accessToken]);
  
  // 获取时间范围
  const fetchBroadcastTime = useCallback(async (timeMode) => {
    const response = await fetch(`${API_BASE}/gift-overlay/obs/${accessToken}/broadcast-time?timeMode=${timeMode}`);
    if (!response.ok) {
      throw new Error('获取直播时间失败');
    }
    return response.json();
  }, [accessToken]);
  
  // 获取礼物数据
  const fetchGifts = useCallback(async (cfg, startTime, endTime) => {
    const params = new URLSearchParams({
      startTime: startTime.toString(),
      endTime: endTime.toString(),
      mergeGifts: cfg.merge_gifts ? 'true' : 'false',
      useNickname: cfg.use_nickname ? 'true' : 'false',
      sortBy: cfg.sort_by || 'time',
      sortOrder: cfg.sort_order || 'desc'
    });
    
    if (cfg.gift_ids?.length > 0) {
      params.append('giftIds', cfg.gift_ids.join(','));
    }
    if (cfg.user_ids?.length > 0) {
      params.append('userIds', cfg.user_ids.join(','));
    }
    if (cfg.min_value) {
      params.append('minValue', cfg.min_value.toString());
    }
    if (cfg.max_value) {
      params.append('maxValue', cfg.max_value.toString());
    }
    
    const response = await fetch(`${API_BASE}/gift-overlay/obs/${accessToken}/gifts?${params}`);
    if (!response.ok) {
      throw new Error('获取礼物数据失败');
    }
    return response.json();
  }, [accessToken]);
  
  // 批量获取用户头像
  const fetchUserFaces = useCallback(async (uids) => {
    if (!uids || uids.length === 0) return {};

    try {
      const response = await fetch(`${API_BASE}/gift-overlay/obs/${accessToken}/user-faces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uids })
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('获取头像失败:', err);
    }
    return {};
  }, [accessToken]);

  // 批量获取用户大航海等级
  const fetchUserGuardLevels = useCallback(async (uids) => {
    if (!uids || uids.length === 0) return {};

    try {
      const response = await fetch(`${API_BASE}/gift-overlay/obs/${accessToken}/user-guard-levels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uids })
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('获取大航海等级失败:', err);
    }
    return {};
  }, [accessToken]);
  
  // 重新加载数据的函数
  const reloadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // 1. 加载配置
      const cfg = await fetchConfig();
      setConfig(cfg);
      setRoomId(cfg.room_id);
      
      // 2. 获取时间范围
      const timeMode = cfg.time_mode || 'current';
      let startTime, endTime;
      
      if (timeMode === 'custom' && cfg.custom_start && cfg.custom_end) {
        startTime = cfg.custom_start;
        endTime = cfg.custom_end;
      } else {
        const broadcastTime = await fetchBroadcastTime(timeMode);
        startTime = broadcastTime.start_time;
        endTime = broadcastTime.end_time;
      }
      
      // 3. 获取礼物数据
      const giftsData = await fetchGifts(cfg, startTime, endTime);
      setGifts(giftsData.gifts || []);
      
      // 4. 获取用户头像和大航海等级
      if (cfg.show_avatar && giftsData.gifts?.length > 0) {
        const uids = [...new Set(giftsData.gifts.map(g => g.uid))];
        const [faces, guardLevels] = await Promise.all([
          fetchUserFaces(uids),
          fetchUserGuardLevels(uids)
        ]);
        setUserFaces(faces);
        setUserGuardLevels(guardLevels);
      } else {
        setUserFaces({});
        setUserGuardLevels({});
      }
    } catch (err) {
      console.error('重新加载数据失败:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchConfig, fetchBroadcastTime, fetchGifts, fetchUserFaces, fetchUserGuardLevels]);

  // 初始化加载
  useEffect(() => {
    if (!accessToken) return;
    
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 1. 加载配置
        const cfg = await fetchConfig();
        setConfig(cfg);
        setRoomId(cfg.room_id);
        
        // 2. 获取时间范围
        const timeMode = cfg.time_mode || 'current';
        let startTime, endTime;
        
        if (timeMode === 'custom' && cfg.custom_start && cfg.custom_end) {
          startTime = cfg.custom_start;
          endTime = cfg.custom_end;
        } else {
          const broadcastTime = await fetchBroadcastTime(timeMode);
          startTime = broadcastTime.start_time;
          endTime = broadcastTime.end_time;
        }
        
        // 3. 获取礼物数据
        const giftsData = await fetchGifts(cfg, startTime, endTime);
        setGifts(giftsData.gifts || []);
        
        // 4. 获取用户头像和大航海等级
        if (cfg.show_avatar && giftsData.gifts?.length > 0) {
          const uids = [...new Set(giftsData.gifts.map(g => g.uid))];
          const [faces, guardLevels] = await Promise.all([
            fetchUserFaces(uids),
            fetchUserGuardLevels(uids)
          ]);
          setUserFaces(faces);
          setUserGuardLevels(guardLevels);
        }
      } catch (err) {
        console.error('加载数据失败:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [accessToken, fetchConfig, fetchBroadcastTime, fetchGifts, fetchUserFaces, fetchUserGuardLevels]);
  
  // WebSocket 订阅
  useEffect(() => {
    if (!roomId || !config) return;
    
    websocketService.subscribeRoom(roomId);
    
    // 监听配置更新
    const handleConfigUpdate = async (data) => {
      if (String(data.data?.room_id) !== String(roomId)) return;
      
      // 实时重新加载配置和数据
      console.log('[GiftOverlay] 配置已更新，重新加载数据...');
      reloadData();
    };
    
    // 批量处理礼物队列
    const processGiftQueue = () => {
      if (giftQueueRef.current.length === 0) return;
      
      const giftsToProcess = [...giftQueueRef.current];
      giftQueueRef.current = [];
      
      setGifts(prevGifts => {
        let updatedGifts = [...prevGifts];
        const newUserUids = [];
        
        for (const gift of giftsToProcess) {
          const giftValue = (parseInt(gift.num) || 0) * (parseInt(gift.price) || 0);
          
          if (config.merge_gifts) {
            // 合并模式：查找并更新已有礼物
            const existingIndex = updatedGifts.findIndex(
              g => String(g.uid) === String(gift.uid) && String(g.gift_id) === String(gift.gift_id)
            );
            
            if (existingIndex >= 0) {
              const existing = updatedGifts[existingIndex];
              const newNum = (parseInt(existing.num) || 0) + (parseInt(gift.num) || 0);
              const price = parseInt(existing.price) || parseInt(gift.price) || 0;
              const newTotalValue = newNum * price;
              
              // 检查合并后的价值是否满足筛选条件
              if (config.min_value && newTotalValue < config.min_value) {
                continue;
              }
              if (config.max_value && newTotalValue > config.max_value) {
                continue;
              }
              
              updatedGifts[existingIndex] = {
                ...existing,
                num: newNum,
                price: price,
                total_coin: (parseInt(existing.total_coin) || 0) + (parseInt(gift.total_coin) || 0),
                last_time: gift.timestamp
              };
              continue;
            }
            
            // 合并模式下新礼物，检查单独价值
            if (config.min_value && giftValue < config.min_value) {
              continue;
            }
            if (config.max_value && giftValue > config.max_value) {
              continue;
            }
          } else {
            // 非合并模式，检查单条礼物价值
            if (config.min_value && giftValue < config.min_value) {
              continue;
            }
            if (config.max_value && giftValue > config.max_value) {
              continue;
            }
          }
          
          // 添加新礼物
          const newGift = {
            ...gift,
            id: Date.now() + Math.random(),
            timestamp: gift.timestamp,
            last_time: gift.timestamp
          };
          updatedGifts.push(newGift);
          
          // 记录需要获取头像的用户
          if (config.show_avatar && !userFaces[gift.uid]) {
            newUserUids.push(gift.uid);
          }
        }
        
        // 获取新用户头像和大航海等级
        if (newUserUids.length > 0) {
          const uniqueUids = [...new Set(newUserUids)];
          Promise.all([
            fetchUserFaces(uniqueUids),
            fetchUserGuardLevels(uniqueUids)
          ]).then(([faces, guardLevels]) => {
            setUserFaces(prev => ({ ...prev, ...faces }));
            setUserGuardLevels(prev => ({ ...prev, ...guardLevels }));
          });
        }
        
        // 排序
        return sortGifts(updatedGifts, config.sort_by, config.sort_order);
      });
    };
    
    // 监听礼物更新（加入队列，延迟批量处理）
    const handleGiftUpdate = (data) => {
      const gift = data.data;
      if (String(gift.room_id) !== String(roomId)) return;
      
      // 检查礼物和用户筛选条件
      if (config.gift_ids?.length > 0 && !config.gift_ids.includes(gift.gift_id)) {
        return;
      }
      if (config.user_ids?.length > 0 && !config.user_ids.includes(gift.uid)) {
        return;
      }
      
      // 加入队列
      giftQueueRef.current.push(gift);
      
      // 清除之前的定时器，重新设置
      if (processTimerRef.current) {
        clearTimeout(processTimerRef.current);
      }
      
      // 延迟 100ms 批量处理，避免快速连续更新
      processTimerRef.current = setTimeout(() => {
        processGiftQueue();
        processTimerRef.current = null;
      }, 100);
    };
    
    websocketService.on('gift_overlay_config_update', handleConfigUpdate);
    websocketService.on('gift_overlay', handleGiftUpdate);
    
    return () => {
      websocketService.off('gift_overlay_config_update', handleConfigUpdate);
      websocketService.off('gift_overlay', handleGiftUpdate);
      websocketService.unsubscribeRoom(roomId);
      
      // 清理定时器
      if (processTimerRef.current) {
        clearTimeout(processTimerRef.current);
      }
    };
  }, [roomId, config, userFaces, fetchUserFaces, fetchUserGuardLevels, reloadData]);
  
  // 计算滚动距离
  useEffect(() => {
    if (!containerRef.current || !contentRef.current || !config) {
      return;
    }
    
    const updateScrollDistance = () => {
      const containerHeight = containerRef.current?.clientHeight || 0;
      const contentHeight = contentRef.current?.scrollHeight || 0;
      
      if (contentHeight > containerHeight && config.scroll_mode !== 'none') {
        let distance;
        
        if (config.scroll_mode === 'loop') {
          // 循环模式：滚动到复制内容的开始位置（原内容高度）
          // 因为内容被复制了一份，所以 contentHeight 是原来的 2 倍
          // 滚动距离 = 原内容高度 = contentHeight / 2
          distance = -(contentHeight / 2);
        } else {
          // 往复模式：滚动到底部
          distance = -(contentHeight - containerHeight);
        }
        
        setShouldScroll(true);
        setScrollDistance(distance);
      } else {
        setShouldScroll(false);
        setScrollDistance(0);
      }
    };
    
    // 延迟执行以确保 DOM 完全渲染
    const timer = setTimeout(updateScrollDistance, 100);
    
    // 监听内容变化
    const observer = new ResizeObserver(updateScrollDistance);
    if (contentRef.current) {
      observer.observe(contentRef.current);
    }
    
    // 监听窗口大小变化
    window.addEventListener('resize', updateScrollDistance);
    
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('resize', updateScrollDistance);
    };
  }, [gifts, config]);
  
  // 计算滚动动画时长
  const scrollDuration = useMemo(() => {
    if (!shouldScroll || !config?.scroll_speed || scrollDistance === 0) {
      return 0;
    }
    // 速度单位：像素/秒
    return Math.abs(scrollDistance) / config.scroll_speed;
  }, [shouldScroll, scrollDistance, config?.scroll_speed]);
  
  // 排序函数
  const sortGifts = (giftsArr, sortBy, sortOrder) => {
    return [...giftsArr].sort((a, b) => {
      let diff = 0;
      switch (sortBy) {
        case 'value':
          // 使用 num * price 计算真实价值进行排序
          diff = (a.num * a.price) - (b.num * b.price);
          break;
        case 'count':
          diff = a.num - b.num;
          break;
        case 'time':
        default:
          diff = (a.last_time || a.timestamp) - (b.last_time || b.timestamp);
      }
      return sortOrder === 'desc' ? -diff : diff;
    });
  };
  
  // 渲染礼物气泡
  const renderGiftBubble = (gift, index) => {
    const levelClass = getGiftLevelClass(gift.num * gift.price);
    // 舰长礼物图标 - 使用 guardIconMap 来显示舰长/提督/总督图标作为礼物图标
    const guardIcon = guardIconMap[gift.gift_name];
    // 使用礼物自带图标，如果是舰长则用舰长图标
    const giftIconUrl = guardIcon || gift.img_basic;
    // 获取用户大航海头像框
    const guardLevel = userGuardLevels[gift.uid];
    const guardFrameUrl = guardFrameMap[guardLevel];


    return (
      <div
        key={gift.id || `${gift.uid}-${gift.gift_id}-${index}`}
        className={`bubble-wrapper ${levelClass}`}
      >
        <div className="bubble-bg"></div>
        <div className="bubble-content">
          {config?.show_avatar && (
            <div className="avatar-wrapper">
              {userFaces[gift.uid] ? (
                <img
                  className="user-avatar"
                  src={userFaces[gift.uid]}
                  alt=""
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="avatar-placeholder"></div>
              )}
              {guardFrameUrl && (
                <img
                  className="guard-frame"
                  src={guardFrameUrl}
                  alt=""
                  referrerPolicy="no-referrer"
                />
              )}
            </div>
          )}
          <div className="gift-info">
            <div className="info-row">
              <span className="user-name">{gift.username}</span>
              <span className="action-text">{guardIcon ? '上舰' : '投喂'}</span>
            </div>
            <span className="gift-name">{gift.gift_name}</span>
          </div>
          {giftIconUrl && (
            <img
              src={giftIconUrl}
              alt=""
              className="gift-icon"
              referrerPolicy="no-referrer"
            />
          )}
          <div className="combo-count">
            <div className="count-part part-x" data-text="x">
              <span className="gradient-text">x</span>
            </div>
            <div className="count-part part-num" data-text={gift.num}>
              <span className="gradient-text">{gift.num}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // 生成 className
  const overlayClass = [
    'gift-overlay',
    isPreview ? 'preview-mode' : '',
    `size-${config?.display_size || 'medium'}`
  ].filter(Boolean).join(' ');
  
  // 滚动样式
  const scrollStyle = shouldScroll ? {
    '--scroll-distance': `${scrollDistance}px`,
    animationDuration: `${scrollDuration}s`
  } : {};
  
  const scrollClass = shouldScroll 
    ? (config?.scroll_mode === 'loop' ? 'scroll-loop' : 'scroll-bounce')
    : '';
  
  // 渲染内容
  const renderContent = () => {
    if (loading) {
      return <div className="loading-hint">加载中...</div>;
    }
    
    if (error) {
      return <div className="error-hint">{error}</div>;
    }
    
    if (gifts.length === 0) {
      return <div className="empty-hint">暂无礼物数据</div>;
    }
    
    // 循环模式时，复制一份内容实现无缝衔接
    const giftElements = gifts.map((gift, index) => renderGiftBubble(gift, index));
    
    if (shouldScroll && config?.scroll_mode === 'loop') {
      // 复制一份内容，用于无缝循环
      const clonedElements = gifts.map((gift, index) => 
        renderGiftBubble(gift, index + gifts.length)
      );
      return [...giftElements, ...clonedElements];
    }
    
    return giftElements;
  };
  
  return (
    <div className={overlayClass}>
      <div className="scroll-container" ref={containerRef}>
        <div 
          className={`scroll-content ${scrollClass}`} 
          ref={contentRef}
          style={scrollStyle}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default GiftOverlay;
